import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Card, CardContent, AppBar, Toolbar, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ConstructionIcon from '@mui/icons-material/Construction';
import FolderIcon from '@mui/icons-material/Folder';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const BRAND = {
  primary: '#1a237e',
  primaryLight: '#283593',
  accent: '#ff6f00',
  gradient: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #283593 100%)',
  light: '#e8eaf6',
};

const NavBar = ({ scrolled, navigate }) => (
  <AppBar position="fixed" elevation={scrolled ? 4 : 0}
    sx={{ bgcolor: scrolled ? '#fff' : 'transparent', transition: 'all 0.3s' }}>
    <Toolbar sx={{ maxWidth: 1200, width: '100%', mx: 'auto', px: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
        <Box sx={{ width: 36, height: 36, bgcolor: scrolled ? BRAND.primary : '#fff', borderRadius: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900,
          color: scrolled ? '#fff' : BRAND.primary, fontSize: 18 }}>Y</Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: scrolled ? BRAND.primary : '#fff',
          letterSpacing: -0.5 }}>Yaplar</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button onClick={() => navigate('/login')}
          sx={{ color: scrolled ? BRAND.primary : '#fff', fontWeight: 600, textTransform: 'none', fontSize: '0.95rem' }}>
          Log In
        </Button>
        <Button variant="contained" onClick={() => navigate('/register')}
          sx={{ bgcolor: scrolled ? BRAND.primary : '#fff', color: scrolled ? '#fff' : BRAND.primary,
            fontWeight: 700, borderRadius: 2, px: 3, textTransform: 'none', fontSize: '0.95rem',
            '&:hover': { bgcolor: scrolled ? BRAND.primaryLight : '#e8eaf6' } }}>
          Get Started
        </Button>
      </Box>
    </Toolbar>
  </AppBar>
);

const Hero = ({ navigate }) => (
  <Box sx={{ background: BRAND.gradient, minHeight: '100vh', display: 'flex', alignItems: 'center',
    position: 'relative', overflow: 'hidden' }}>
    <Box sx={{ position: 'absolute', top: '10%', right: '-5%', width: 500, height: 500,
      borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.03)' }} />
    <Box sx={{ position: 'absolute', bottom: '-10%', left: '-5%', width: 400, height: 400,
      borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.02)' }} />
    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 12, md: 0 } }}>
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={7}>
          <Box sx={{ bgcolor: 'rgba(255,111,0,0.15)', display: 'inline-block', borderRadius: 5, px: 2, py: 0.5, mb: 3 }}>
            <Typography variant="caption" sx={{ color: '#ffcc02', fontWeight: 700, fontSize: '0.85rem', letterSpacing: 1 }}>
              CONSTRUCTION MANAGEMENT PLATFORM
            </Typography>
          </Box>
          <Typography variant="h1" sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: '2.5rem', md: '3.8rem' },
            lineHeight: 1.1, mb: 3, letterSpacing: -1 }}>
            Build Smarter.<br />Manage Better.<br />
            <Box component="span" sx={{ color: '#ffcc02' }}>Get Paid Faster.</Box>
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 400, mb: 4,
            maxWidth: 520, lineHeight: 1.7, fontSize: '1.15rem' }}>
            Yaplar connects homeowners, general contractors, and subcontractors on one platform —
            from project kickoff to final payment.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" size="large" onClick={() => navigate('/register')}
              sx={{ bgcolor: BRAND.accent, color: '#fff', fontWeight: 700, borderRadius: 2, px: 5, py: 1.5,
                fontSize: '1.05rem', textTransform: 'none', boxShadow: '0 4px 20px rgba(255,111,0,0.4)',
                '&:hover': { bgcolor: '#e65100' } }}>
              Get Started Free
            </Button>
            <Button variant="outlined" size="large" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              sx={{ borderColor: 'rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600, borderRadius: 2,
                px: 4, py: 1.5, fontSize: '1.05rem', textTransform: 'none',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              Learn More
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 4, mt: 5 }}>
            {[{ val: '$50M+', label: 'Invoices Processed' }, { val: '99%', label: 'Uptime' }].map(s => (
              <Box key={s.label}>
                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800 }}>{s.val}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 4, p: 4, backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.12)' }}>
            {[{ icon: <FolderIcon />, text: 'Projects & Budgets', color: '#4fc3f7' },
              { icon: <DescriptionIcon />, text: 'Contracts & Proposals', color: '#ffcc02' },
              { icon: <ReceiptIcon />, text: 'Invoices & Payments', color: '#69f0ae' },
              { icon: <CalendarTodayIcon />, text: 'Scheduling & Tasks', color: '#ff8a65' },
              { icon: <GroupIcon />, text: 'Team Collaboration', color: '#ce93d8' },
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5,
                borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                <Box sx={{ bgcolor: `${item.color}20`, borderRadius: 2, p: 1, display: 'flex' }}>
                  {React.cloneElement(item.icon, { sx: { color: item.color, fontSize: 24 } })}
                </Box>
                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '1rem' }}>{item.text}</Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

const roleCards = [
  {
    icon: <HomeIcon />, title: 'Homeowners', color: '#1565c0', bg: '#e3f2fd',
    tagline: 'Full visibility into your project',
    features: ['Track project progress & budgets', 'Review and approve invoices', 'Manage contracts & payments', 'View construction schedule'],
  },
  {
    icon: <EngineeringIcon />, title: 'General Contractors', color: '#e65100', bg: '#fff3e0',
    tagline: 'Run projects like a pro',
    features: ['Manage subcontractors & vendors', 'Create contracts & purchase orders', 'Track invoices & payments', 'Schedule tasks & milestones'],
  },
  {
    icon: <ConstructionIcon />, title: 'Subcontractors', color: '#2e7d32', bg: '#e8f5e9',
    tagline: 'Win work and get paid faster',
    features: ['Browse & bid on open scopes', 'Submit proposals & invoices', 'Track payment status', 'Send documents via email'],
  },
];

const Features = () => (
  <Box id="features" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8f9ff' }}>
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography variant="overline" sx={{ color: BRAND.accent, fontWeight: 700, letterSpacing: 2, fontSize: '0.85rem' }}>
          BUILT FOR EVERYONE
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 900, color: BRAND.primary, mt: 1, mb: 2, fontSize: { xs: '2rem', md: '2.8rem' } }}>
          One Platform, Three Perspectives
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', maxWidth: 600, mx: 'auto', fontSize: '1.1rem' }}>
          Whether you're building your dream home, managing crews, or bidding on jobs — Yaplar keeps everyone aligned.
        </Typography>
      </Box>
      <Grid container spacing={4}>
        {roleCards.map(role => (
          <Grid item xs={12} md={4} key={role.title}>
            <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 4, height: '100%',
              transition: 'all 0.3s', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 16px 40px rgba(26,35,126,0.12)' } }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ bgcolor: role.bg, borderRadius: 3, width: 56, height: 56, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                  {React.cloneElement(role.icon, { sx: { color: role.color, fontSize: 28 } })}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: BRAND.primary, mb: 0.5 }}>{role.title}</Typography>
                <Typography variant="body2" sx={{ color: role.color, fontWeight: 600, mb: 3 }}>{role.tagline}</Typography>
                {role.features.map(f => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <CheckCircleIcon sx={{ color: role.color, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#555' }}>{f}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

const steps = [
  { num: '01', title: 'Create Your Project', desc: 'Set up your project with budget, location, and timeline details.' },
  { num: '02', title: 'Build Your Team', desc: 'Invite contractors, assign roles, and manage subcontractor access.' },
  { num: '03', title: 'Manage the Work', desc: 'Create scopes, contracts, schedules, and track progress in real-time.' },
  { num: '04', title: 'Track Payments', desc: 'Submit invoices, approve payments, and keep financials transparent.' },
];

const HowItWorks = () => (
  <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff' }}>
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography variant="overline" sx={{ color: BRAND.accent, fontWeight: 700, letterSpacing: 2, fontSize: '0.85rem' }}>
          SIMPLE & POWERFUL
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 900, color: BRAND.primary, mt: 1, fontSize: { xs: '2rem', md: '2.8rem' } }}>
          How Yaplar Works
        </Typography>
      </Box>
      <Grid container spacing={4}>
        {steps.map((step, i) => (
          <Grid item xs={12} sm={6} md={3} key={step.num}>
            <Box sx={{ textAlign: 'center', position: 'relative' }}>
              <Typography sx={{ fontSize: '4rem', fontWeight: 900, color: BRAND.light, lineHeight: 1 }}>{step.num}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: BRAND.primary, mt: -1, mb: 1 }}>{step.title}</Typography>
              <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>{step.desc}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

const CtaSection = ({ navigate }) => (
  <Box sx={{ background: BRAND.gradient, py: { xs: 8, md: 10 }, textAlign: 'center' }}>
    <Container maxWidth="md">
      <Typography variant="h3" sx={{ color: '#fff', fontWeight: 900, mb: 2, fontSize: { xs: '2rem', md: '2.8rem' } }}>
        Ready to Streamline Your Construction Projects?
      </Typography>
      <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 400, mb: 4, maxWidth: 500, mx: 'auto' }}>
        Join hundreds of teams already using Yaplar to build better, together.
      </Typography>
      <Button variant="contained" size="large" onClick={() => navigate('/register')}
        sx={{ bgcolor: BRAND.accent, color: '#fff', fontWeight: 700, borderRadius: 2, px: 6, py: 1.5,
          fontSize: '1.1rem', textTransform: 'none', boxShadow: '0 4px 20px rgba(255,111,0,0.4)',
          '&:hover': { bgcolor: '#e65100' } }}>
        Get Started Free
      </Button>
    </Container>
  </Box>
);

const Footer = ({ navigate }) => (
  <Box sx={{ bgcolor: '#0a1045', py: 6 }}>
    <Container maxWidth="lg">
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 32, height: 32, bgcolor: '#fff', borderRadius: 1.5, display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: BRAND.primary, fontSize: 16 }}>Y</Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800 }}>Yaplar</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Construction management, simplified.
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: { md: 'center' } }}>
            {['Features', 'How It Works'].map(item => (
              <Typography key={item} variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
                '&:hover': { color: '#fff' } }}
                onClick={() => document.getElementById(item === 'Features' ? 'features' : 'how-it-works')?.scrollIntoView({ behavior: 'smooth' })}>
                {item}
              </Typography>
            ))}
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer', '&:hover': { color: '#fff' } }}
              onClick={() => navigate('/login')}>Log In</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer', '&:hover': { color: '#fff' } }}
              onClick={() => navigate('/register')}>Sign Up</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', textAlign: { md: 'right' } }}>
            &copy; {new Date().getFullYear()} Yaplar. All rights reserved.
          </Typography>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box sx={{ bgcolor: '#fff' }}>
      <NavBar scrolled={scrolled} navigate={navigate} />
      <Hero navigate={navigate} />
      <Features />
      <HowItWorks />
      <CtaSection navigate={navigate} />
      <Footer navigate={navigate} />
    </Box>
  );
}
